// src/content/figures/ShopRepresentativeDecision.jsx
// Inline SVG infographic: does an online store need an EU representative?
// Real text (indexable, selectable, screen-reader accessible).
// Explicit colours so it renders predictably regardless of theme tokens.

const C = {
  ink: '#1e293b',
  muted: '#5b6472',
  line: '#94a3b8',
  boxFill: '#f3f6fb',
  boxStroke: '#cbd5e1',
  white: '#ffffff',
  accent: '#186be6',
};

const q = { fill: C.boxFill, stroke: C.boxStroke, strokeWidth: 1.25, rx: 10 };
const end = { fill: C.white, stroke: C.boxStroke, strokeWidth: 1.25, rx: 10 };
const win = { fill: C.accent, stroke: 'none', rx: 10 };
const t = { fontFamily: 'inherit', fontSize: 15, fill: C.ink };
const s = { fontFamily: 'inherit', fontSize: 13, fill: C.muted };
const lab = { fontFamily: 'inherit', fontSize: 13, fill: C.ink, fontWeight: 600 };
const onAccent = { fontFamily: 'inherit', fontSize: 15, fill: C.white };
const edge = { stroke: C.line, strokeWidth: 1.5, fill: 'none', markerEnd: 'url(#shop-arrow)' };

export default function ShopRepresentativeDecision() {
  return (
    <svg
      viewBox="0 0 900 720"
      width="100%"
      role="img"
      aria-labelledby="shop-title shop-desc"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', maxWidth: 900 }}
    >
      <title id="shop-title">Does your online store need an EU representative?</title>
      <desc id="shop-desc">
        Decision flow. One: do you ship or sell to people in the EU? An EU shipping zone is a strong sign; a site people can merely reach is not. If yes, go to question three. If no, question two: do you run pixels, email flows or analytics on EU visitors to follow what individual shoppers do? If no, you need no EU representative; re-check if you add an EU shipping zone. If yes, go to question three: do you have your own office or company in the EU? A shipping zone or euro prices do not count. If yes, this rule does not apply, because it covers only stores run from outside the EU. If no, question four: are all three true, namely the handling is only occasional and not part of your normal business, there is no sensitive data or criminal records at scale, and it is unlikely to put people at risk? Few orders is not occasional; an EU shipping zone open all year is regular. If all three are true you are exempt, which is rare for a live store; write down why and re-check. Otherwise, appoint an EU representative and name it in your privacy policy. Side note: in Shopify, the privacy policy is under Settings, Policies.
      </desc>
      <defs>
        <marker id="shop-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={C.line} />
        </marker>
      </defs>

      {/* Column guide: left x=20-250 (c=135) · centre x=270-630 (c=450; Q3 280-620; Q4 wide 250-650) · right x=660-890 (c=775) */}

      {/* Q1 */}
      <rect x="270" y="20" width="360" height="90" {...q} />
      <text x="450" y="52" textAnchor="middle" {...t}>Do you ship or sell to people in the EU?</text>
      <text x="450" y="76" textAnchor="middle" {...s}>An EU shipping zone is a strong sign.</text>
      <text x="450" y="95" textAnchor="middle" {...s}>A site people can merely reach is not.</text>

      {/* Q1 → Yes (down to Q3) */}
      <path d="M 450 110 L 450 290" {...edge} />
      <text x="462" y="205" {...lab}>Yes</text>

      {/* Q1 → No (right, then down to Q2) */}
      <path d="M 630 65 L 775 65 L 775 150" {...edge} />
      <text x="650" y="56" {...lab}>No</text>

      {/* Q2 */}
      <rect x="660" y="150" width="230" height="116" {...q} />
      <text x="775" y="174" textAnchor="middle" {...t}>Do you run pixels,</text>
      <text x="775" y="193" textAnchor="middle" {...t}>email flows or</text>
      <text x="775" y="212" textAnchor="middle" {...t}>analytics on EU visitors</text>
      <text x="775" y="234" textAnchor="middle" {...s}>to follow what</text>
      <text x="775" y="250" textAnchor="middle" {...s}>individual shoppers do?</text>

      {/* Q2 → Yes (left, then down into Q3) */}
      <path d="M 660 205 L 560 205 L 560 290" {...edge} />
      <text x="612" y="196" {...lab}>Yes</text>

      {/* Q2 → No (down) */}
      <path d="M 775 266 L 775 296" {...edge} />
      <text x="787" y="286" {...lab}>No</text>

      {/* Q2a */}
      <rect x="660" y="296" width="230" height="96" {...end} />
      <text x="775" y="322" textAnchor="middle" {...t}>No EU representative</text>
      <text x="775" y="342" textAnchor="middle" {...t}>needed.</text>
      <text x="775" y="365" textAnchor="middle" {...s}>Re-check if you add</text>
      <text x="775" y="382" textAnchor="middle" {...s}>an EU shipping zone.</text>

      {/* Q3 */}
      <rect x="280" y="290" width="340" height="86" {...q} />
      <text x="450" y="318" textAnchor="middle" {...t}>Do you have your own office</text>
      <text x="450" y="339" textAnchor="middle" {...t}>or company in the EU?</text>
      <text x="450" y="361" textAnchor="middle" {...s}>A shipping zone or euro prices do not count.</text>

      {/* Q3 → Yes (left) */}
      <path d="M 280 333 L 250 333" {...edge} />
      <text x="254" y="324" {...lab}>Yes</text>
      <rect x="20" y="288" width="230" height="90" {...end} />
      <text x="135" y="318" textAnchor="middle" {...t}>This rule does not apply.</text>
      <text x="135" y="341" textAnchor="middle" {...s}>It covers only stores run</text>
      <text x="135" y="359" textAnchor="middle" {...s}>from outside the EU.</text>

      {/* Q3 → No (down) */}
      <path d="M 450 376 L 450 420" {...edge} />
      <text x="462" y="403" {...lab}>No</text>

      {/* Q4 */}
      <rect x="250" y="420" width="400" height="152" {...q} />
      <text x="450" y="446" textAnchor="middle" {...t}>Are all three true?</text>
      <text x="450" y="470" textAnchor="middle" {...s}>1. Only occasional, not part of your normal business</text>
      <text x="450" y="490" textAnchor="middle" {...s}>2. No sensitive data or criminal records at scale</text>
      <text x="450" y="510" textAnchor="middle" {...s}>3. Unlikely to put people at risk</text>
      <rect x="266" y="522" width="368" height="38" fill="#fff4e0" stroke="none" rx="6" />
      <text x="450" y="538" textAnchor="middle" fontFamily="inherit" fontSize="12.5" fill="#7a4a00" fontWeight="600">Few orders is not "occasional".</text>
      <text x="450" y="553" textAnchor="middle" fontFamily="inherit" fontSize="12.5" fill="#7a4a00">An EU shipping zone open all year is regular.</text>

      {/* Q4 → Yes, all three (left) */}
      <path d="M 250 496 L 135 496 L 135 620" {...edge} />
      <text x="147" y="580" {...lab}>Yes, all three</text>
      <rect x="20" y="620" width="230" height="80" {...end} />
      <text x="135" y="648" textAnchor="middle" {...t}>Exempt.</text>
      <text x="135" y="670" textAnchor="middle" {...s}>Rare for a live store.</text>
      <text x="135" y="688" textAnchor="middle" {...s}>Write down why; re-check.</text>

      {/* Q4 → No (down) */}
      <path d="M 450 572 L 450 620" {...edge} />
      <text x="462" y="601" {...lab}>No</text>

      {/* Result */}
      <rect x="290" y="620" width="320" height="80" {...win} />
      <text x="450" y="653" textAnchor="middle" {...onAccent} fontWeight="600">Appoint an EU representative</text>
      <text x="450" y="676" textAnchor="middle" {...onAccent}>and name it in your privacy policy</text>

      {/* Result - - Shopify note (dashed, no arrowhead) */}
      <path d="M 610 660 L 660 660" stroke={C.line} strokeWidth="1.5" strokeDasharray="4 4" fill="none" />
      <rect x="660" y="625" width="230" height="70" {...end} />
      <text x="775" y="653" textAnchor="middle" {...t}>In Shopify:</text>
      <text x="775" y="674" textAnchor="middle" {...s}>Settings &gt; Policies</text>
    </svg>
  );
}
